/**
 * Claude subscription-key usage sampler.
 *
 * Reports the session/weekly rate-limit utilization of the key the pod runs
 * under. The numbers come from the Claude CLI's own `/usage` panel (driven under
 * a PTY — see run-probe.ts) — the authoritative, account-wide gauges the CLI
 * itself shows. Mapped into the `rate_limit_update` events the API already
 * persists (`persistRateLimitSnapshot` →
 * ClaudeSubscriptionKey.sessionUsage/weeklyUsage + usageGauges), which keeps
 * User Settings, the usage widget, and `selectBestKey` rotation honest.
 *
 * Attribution guard: the probe authenticates from `.credentials.json`, but the
 * server attributes samples to the key stamped on THIS session. If the file is
 * authenticated as someone else — a real interactive /login (refreshToken
 * present) or a token that doesn't match the session's own — reporting would
 * write another account's gauges onto this key's row, so the sample is skipped.
 *
 * Best-effort: any probe/parse failure yields `[]`, never throws.
 */
import { existsSync } from "node:fs";
import { createServiceLogger } from "../utils/logger.js";
import {
  claudeCredentialsPath,
  readCredentialsIdentity,
  type CredentialsIdentity,
} from "../harness/pty/credentials.js";
import { parseUsageGauges, type UsageGaugeReading } from "../usage/parse-usage.js";
import { runUsageProbe } from "../usage/run-probe.js";

const logger = createServiceLogger("usage-sampler");

export interface KeyUsageSample {
  rateLimitType: string;
  /** 0..1 utilization fraction (matches UsageBar / getRateLimitColor). */
  utilization: number;
  status: string;
  /** Full labeled gauge set from the probe (session + per-model weeklies). */
  gauges: UsageGaugeReading[];
}

/**
 * Decide whether the credentials file's identity may be attributed to this
 * session's key. Pure so the skip rules are unit-testable.
 */
export function isAttributable(
  identity: CredentialsIdentity | null,
  sessionToken: string | undefined,
): { ok: boolean; reason?: string } {
  if (!identity) return { ok: true };
  if (identity.hasRefreshToken) {
    // A real /login owns the file; we cannot know which key row (if any) that
    // account corresponds to.
    return { ok: false, reason: "manual-login-credentials" };
  }
  if (sessionToken && identity.accessToken && identity.accessToken !== sessionToken) {
    return { ok: false, reason: "credentials-token-mismatch" };
  }
  return { ok: true };
}

/**
 * Sample the current key's usage via the `/usage` panel and map it to rate-limit
 * samples. Sampling runs only on **subscription** pods: an API-key pod has no
 * `/usage` gauges, so it no-ops.
 *
 * The subscription signal is an OAuth token in env OR a synthesized
 * `~/.claude/.credentials.json`. Gating on the env token ALONE silently disabled
 * this on v3 Claudespace pods, where auth is the credentials file and
 * `CLAUDE_CODE_OAUTH_TOKEN` is NOT in the agent env — the direct cause of usage
 * stats never updating there.
 *
 * `probe`/`hasSubscriptionCredentials`/`readIdentity` are injectable for tests;
 * production callers pass only `token`. Best-effort — never throws.
 */
export async function sampleKeyUsage(
  token: string | undefined,
  probe: () => Promise<string> = () => runUsageProbe(),
  hasSubscriptionCredentials: () => boolean = () => existsSync(claudeCredentialsPath()),
  readIdentity: () => Promise<CredentialsIdentity | null> = readCredentialsIdentity,
): Promise<KeyUsageSample[]> {
  if (!token && !hasSubscriptionCredentials()) return [];
  try {
    const attributable = isAttributable(await readIdentity(), token);
    if (!attributable.ok) {
      logger.info("usage sample skipped — credentials not attributable to this session's key", {
        reason: attributable.reason,
      });
      return [];
    }

    const stdout = await probe();
    const { sessionUsage, weeklyUsage, gauges } = parseUsageGauges(stdout);
    const samples: KeyUsageSample[] = [];
    if (sessionUsage !== null) {
      samples.push({
        rateLimitType: "five_hour",
        utilization: sessionUsage,
        status: "allowed",
        gauges,
      });
    }
    if (weeklyUsage !== null) {
      samples.push({
        rateLimitType: "seven_day",
        utilization: weeklyUsage,
        status: "allowed",
        gauges,
      });
    }
    if (samples.length === 0) {
      // Log a head of the actual output — a deterministic no-gauge reply (e.g.
      // the CLI refusing /usage under env-token auth) is invisible from the
      // length alone and left this pipeline silently dead for months.
      logger.info("usage sample produced no gauges", {
        stdoutLength: stdout.length,
        stdoutHead: stdout.slice(0, 200).replaceAll("\n", " "),
      });
    }
    return samples;
  } catch (error) {
    logger.info("usage sample failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
}
